import { z } from 'zod'
import {
  SOLUTION_PRICING_BASES,
  solutionPricingBasisSchema,
} from '../enums/solution-pricing-basis.enum.js'
import { seasonSchema } from '../enums/season.enum.js'

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((value) => (value === '' ? null : value))
    .nullish()

/**
 * A rate, as an exact decimal string.
 *
 * Same treatment as every other money value here: a string all the way to the
 * `Decimal` column, never through a float. Null means "no rate set", which is
 * only meaningful for TIME — the service refuses it on the others, because a
 * rate of nothing is not a rate.
 */
const rateSchema = z
  .string()
  .trim()
  .regex(/^$|^\d{1,6}(\.\d{1,2})?$/, 'Enter a rate in dollars, e.g. 85 or 4.50')
  .transform((value) => (value === '' ? null : value))
  .nullable()

/**
 * How a solution is charged, one entry per basis.
 *
 * An empty list is allowed and means the treatment costs nothing to record —
 * "monitor annually" is real work to note and nothing to bill. At most one
 * entry per basis: "one or more of these", not "any number of these", so a
 * solution cannot be charged twice by the foot.
 */
const pricingsSchema = z
  .array(
    z
      .object({
        basis: solutionPricingBasisSchema,
        unitRate: rateSchema,
      })
      .strict()
      .refine(
        (pricing) => pricing.basis === 'TIME' || pricing.unitRate !== null,
        {
          message: 'Set a rate — only time can fall back to the crew rate',
          path: ['unitRate'],
        },
      ),
  )
  // One of each basis at most, so never more entries than there are bases.
  .max(SOLUTION_PRICING_BASES.length)
  .refine((pricings) => {
    const bases = pricings.map((pricing) => pricing.basis)
    return new Set(bases).size === bases.length
  }, 'A solution cannot be charged twice on the same basis')

export const createProblemSolutionInputSchema = z
  .object({
    label: z.string().trim().min(1, 'A solution needs a label').max(120),
    description: optionalText(4000),
    /** Sent whole: the list supplied replaces whatever the solution carried. */
    pricings: pricingsSchema.default([]),
    sortOrder: z.number().int().min(0).max(9999).optional(),
    /** The season it is usually done in; null or absent for none. */
    usualSeason: seasonSchema.nullish(),
  })
  .strict()

export type CreateProblemSolutionInput = z.infer<
  typeof createProblemSolutionInputSchema
>

/** Every field optional; absent means "leave it alone". */
export const updateProblemSolutionInputSchema = z
  .object({
    label: z.string().trim().min(1, 'A solution needs a label').max(120),
    description: optionalText(4000),
    pricings: pricingsSchema,
    sortOrder: z.number().int().min(0).max(9999),
    usualSeason: seasonSchema.nullable(),
  })
  .partial()
  .strict()

export type UpdateProblemSolutionInput = z.infer<
  typeof updateProblemSolutionInputSchema
>
